import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { ButtonComponent, Color } from '@mode/capra';
import { LoaderComponent, StatusBannerComponent } from '@mode/shared/ui';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { ScheduleListDescriptionTileComponent } from '../schedule-list-description-tile/schedule-list-description-tile.component';
import { ScheduleListActionsComponent } from '../schedule-list-actions/schedule-list-actions.component';
import { ReportSchedule, ReportSchedulesFacade } from '@mode/shared/schedules/contract';
import { DatasetFacade } from '@mode/dataset/contract';
import { AccountFacade } from '@mode/accounts/contract';
import { combineLatestWith, filter, map } from 'rxjs/operators';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    MatLegacyMenuModule,
    MatDividerModule,
    ScheduleListDescriptionTileComponent,
    ScheduleListActionsComponent,
    LoaderComponent,
    StatusBannerComponent,
  ],
  selector: 'mode-schedule-list-container',
  templateUrl: './schedule-list-container.component.html',
  styleUrls: ['./schedule-list-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleListContainerComponent implements OnChanges {
  constructor(
    public datasetFacade: DatasetFacade,
    public reportSchedulesFacade: ReportSchedulesFacade,
    private _accountFacade: AccountFacade,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {}

  public readonly neutralButtonColor: Color = 'neutral';
  public readonly isAnalystStudio = this.thoughtspotIntegrationService.currOrgIntegrated();

  @Input() reportSchedulesEnabled = true;
  @Input() report = {
    token: '',
    type: 'Report',
    _resource: {
      _forms: {
        edit: { method: 'patch', action: 'actionUrl' },
      },
    },
  };
  @Input() subscriberEmail = 'none@email.com';
  @Input() workspaceTitle = 'workspaceTitle';
  @Output() createNewSchedule = new EventEmitter<void>();
  @Output() editLegacySchedule = new EventEmitter<ReportSchedule>();
  @Output() loadLegacySchedules = new EventEmitter<void>();
  hasEditAcces = false;
  initializingSchedules = false;

  canCreateSchedule = true;

  googleSheetsEnabled$ = this._accountFacade.currentOrg$.pipe(
    combineLatestWith([this.datasetFacade.isSheetDataset$]),
    filter(([, isSheetDataset]) => !!isSheetDataset),
    map(([currentOrg]) => !!currentOrg.googleSheetsEnabled)
  );

  ngOnChanges(): void {
    this.hasEditAcces = !!this.report._resource._forms.edit;
    this.reportSchedulesFacade.loadReportSchedules({ reportId: this.report.token });
  }

  deleteSchedule(scheduleId: string): void {
    const deleteEmailPayload = { reportId: this.report.token, scheduleId: scheduleId };
    this.reportSchedulesFacade.deleteReportSchedule(deleteEmailPayload);
  }

  createOrDeleteSubscription(schedule: ReportSchedule): void {
    if (schedule?.emailMemberships?.deleteUrl) {
      this.reportSchedulesFacade.deleteEmailSubscription({
        url: <string>schedule.emailMemberships?.deleteUrl,
        scheduleId: schedule.id,
        subscriberCount: schedule.subscriberCount - 1,
      });
    } else {
      this.reportSchedulesFacade.createEmailSubscription({
        url: <string>schedule.subscription?.createEmailSubscriptionUrl,
        emailPayload: { membership: { subscriber_email: this.subscriberEmail } },
        scheduleId: schedule.id,
        subscriberCount: schedule.subscriberCount + 1,
      });
    }
  }

  editSchedule(schedule: ReportSchedule) {
    this.editLegacySchedule.emit(schedule);
  }
}
